import { ref, watch, nextTick, onBeforeUnmount, type Ref } from "vue";

type UseFormInputsOptions = {
  selector?: string;
  attribute?: string;
  value?: string;
  observeOptions?: MutationObserverInit;
  onFound?: (el: HTMLElement, via: "initial" | "mutation" | "fallback") => void;
  checkForRequiredInputs?: boolean;
};

export function useFormAttributes(opts: UseFormInputsOptions = {}) {
  const {
    selector = [
      "input",
      "textarea",
      "select",
      '[role="combobox"]',
      '[role="radio"]',
      '[role="checkbox"]',
      "button",
      "a[href]",
      "[contenteditable='true']",
      "[tabindex]:not([tabindex='-1'])",
    ].join(", "),
    attribute = "aria-describedby",
    value = "form-required-text-description",
    observeOptions = { childList: true, subtree: true },
    onFound,
    checkForRequiredInputs = false,
  } = opts;

  const containerEl: Ref<HTMLElement | null> = ref(null);
  let observer: MutationObserver | null = null;
  const requiredFieldsCount = ref<number>(0);

  function addAttributeToElement(el: HTMLElement, attr: string, val: string) {
    if (checkForRequiredInputs && !requiredFieldsCount.value) {
      return;
    } else if (attr === "aria-describedby") {
      const before = (el.getAttribute(attr) || "").trim();
      const next = [val, before].filter(Boolean).join(" ");
      el.setAttribute(attr, next);
      return;
    } else {
      el.setAttribute(attr, val);
    }
  }

  function pickFirstInDom(root: ParentNode): HTMLElement | null {
    return root.querySelector(selector) as HTMLElement | null;
  }

  function findFirstInputFromMutations(mutations: unknown): HTMLElement | null {
    const list: MutationRecord[] = Array.isArray(mutations)
      ? mutations
      : Array.from((mutations as any) ?? []);

    for (const m of list) {
      if (!m || m.type !== "childList") continue;
      const added = Array.from(m.addedNodes ?? []);
      for (const node of added) {
        if (!(node instanceof Element)) continue;

        if (node.matches?.(selector)) return node as HTMLElement;
        const nested = node.querySelector?.(selector) as HTMLElement | null;
        if (nested) return nested;
      }
    }
    return null;
  }

  function updateRequiredCount(root: HTMLElement | null) {
    if (!root) {
      requiredFieldsCount.value = 0;
      return;
    }
    const q = [
      'input[required]:not([required="false"]):not([required="0"]):not([required="off"])',
      'fieldset[required]:not([required="false"]):not([required="0"]):not([required="off"])',
      'input[aria-required="true"]',
      'fieldset[aria-required="true"]',
    ].join(", ");

    requiredFieldsCount.value = root.querySelectorAll(q).length;
  }

  function teardown() {
    if (observer) {
      observer.disconnect();
      observer = null;
    }
  }

  function initialScan(el: HTMLElement) {
    // does the form have any required fields?
    updateRequiredCount(el);

    const tryScan = (via: "initial" | "fallback") => {
      const first = pickFirstInDom(el);
      if (first) {
        addAttributeToElement(first, attribute, value);
        onFound?.(first, via);
        return true;
      }
      return false;
    };

    if (!tryScan("initial")) {
      nextTick(() => {
        if (!tryScan("fallback")) {
          requestAnimationFrame(() => {
            tryScan("fallback");
          });
        }
      });
    }
  }

  function attach(el: HTMLElement) {
    teardown();
    initialScan(el);

    observer = new MutationObserver((mutationsList) => {
      const found = findFirstInputFromMutations(mutationsList);
      if (found) {
        addAttributeToElement(found, attribute, value);
        onFound?.(found, "mutation");
      } else {
        const fallback = pickFirstInDom(el);
        if (fallback) {
          addAttributeToElement(fallback, attribute, value);
          onFound?.(fallback, "fallback");
        }
      }

      // update required count after any mutation batch
      updateRequiredCount(el);
    });

    observer.observe(el, observeOptions);
  }

  watch(
    containerEl,
    async (el) => {
      await nextTick();
      if (!el) {
        teardown();
        requiredFieldsCount.value = 0;
        return;
      }
      attach(el);
    },
    { immediate: true, flush: "post" },
  );

  onBeforeUnmount(teardown);

  return {
    containerEl,
    requiredFieldsCount, // 👈 expose the count
    start: () => {
      if (containerEl.value) attach(containerEl.value);
    },
    stop: () => teardown(),
    isObserving: () => observer !== null,
    findNow: () => {
      const root = containerEl.value;
      if (!root) return null;
      const el = pickFirstInDom(root);
      if (el) addAttributeToElement(el, attribute, value);
      // keep count fresh on demand calls too
      updateRequiredCount(root);
      return el;
    },
  } as const;
}
